import {
  usePendingMutationCount,
  useSyncStatus,
  useLiveQuery,
  useLocalSaveState,
} from "@hq/client-react";
import { cn, formatShortcut, IconButton, Tooltip, useShortcut } from "@z/ui";
import { Inbox, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useAccountSnapshot } from "../auth/useAccountSnapshot";
import { useAppConfig } from "../AppConfig";
import { SIDEBAR_ID, useSidebarState } from "./useSidebarState";
import { useQuickCapture } from "../actions/QuickCaptureContext";

function CaptureButton() {
  const capture = useQuickCapture();
  const shortcut = useShortcut("tasks.new");
  return (
    <Tooltip content="Quick capture to Inbox" shortcutId="tasks.new">
      <button
        type="button"
        aria-label="Quick capture to Inbox"
        onClick={capture.open}
        className="flex h-7 shrink-0 items-center gap-1.5 px-2 text-xs text-fg-muted transition-colors hover:bg-fg/6 hover:text-fg focus-ring-inset"
      >
        <Inbox aria-hidden="true" className="size-3.5" />
        Capture
        <span className="hidden sm:inline">{shortcut ? formatShortcut(shortcut) : null}</span>
      </button>
    </Tooltip>
  );
}

function SidebarToggle() {
  const { collapsed, toggle } = useSidebarState();
  const Icon = collapsed ? PanelLeftOpen : PanelLeftClose;
  return (
    <IconButton
      label={collapsed ? "Open sidebar" : "Collapse sidebar"}
      aria-expanded={!collapsed}
      aria-controls={SIDEBAR_ID}
      onClick={toggle}
      className="hidden h-7 w-9 rounded-none border-r border-border text-fg-muted lg:inline-flex"
    >
      <Icon aria-hidden="true" strokeWidth={1.75} />
    </IconButton>
  );
}

/** One line: where the data is, and whether anything is waiting to sync. */
export function StatusBar() {
  const status = useSyncStatus();
  const localSave = useLocalSaveState();
  const paletteShortcut = useShortcut("palette.toggle");
  const noteShortcut = useShortcut("notes.new");
  const conflicts =
    useLiveQuery(["kv"], (s) => s.kv.get<unknown[]>("sync:conflicts"), []).data?.length ?? 0;
  const pending = usePendingMutationCount().data ?? 0;
  const { hasServer, account } = useAppConfig();
  const session = useAccountSnapshot(account);
  let text: string;
  if (localSave === "error") text = "Save failed · changes not saved";
  else if (localSave === "saving") text = "Saving on this device…";
  else if (!hasServer) text = "Saved on this device";
  else if (account && session.phase !== "signed_in")
    text = "Saved on this device · sign in to sync";
  else if (status.updateRequired) text = "Update required";
  else if (status.phase === "offline")
    text = pending > 0 ? `Offline · ${pending} pending` : "Offline";
  else if (status.phase === "pushing" || status.phase === "pulling") text = "Syncing…";
  else if (status.phase === "error") text = `Sync error · ${pending} pending`;
  else text = pending > 0 ? `${pending} pending` : status.lastPullAt ? "Synced" : "Connecting…";
  if (conflicts) text += ` · ${conflicts} conflict${conflicts === 1 ? "" : "s"} to review`;
  return (
    <footer className="flex h-7 shrink-0 items-center justify-between border-t border-border bg-surface pr-3 text-fg-muted text-xs">
      <div className="flex min-w-0 items-center">
        <SidebarToggle />
        <span
          data-sync-status
          data-save-status={localSave}
          role="status"
          className={cn(
            "inline-flex items-center gap-2 px-3",
            localSave === "error" && "text-danger",
          )}
        >
          <span aria-hidden="true" className="size-1.5 rounded-full bg-current opacity-60" />
          {text}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-3">
        <span className="hidden lg:inline">
          {[
            paletteShortcut ? `${formatShortcut(paletteShortcut)} search` : null,
            noteShortcut ? `${formatShortcut(noteShortcut)} new note` : null,
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
        <CaptureButton />
      </div>
    </footer>
  );
}
